import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    makeStyles,
    Theme,
} from '@material-ui/core'
import React, { ReactNode } from 'react'
import AIIcon from 'resources/AIIcon'

const AI_ICON_COLOR = '#8A30BE'

/** Props for {@link AIDisclaimerDialog}. */
export interface AIDisclaimerDialogProps {
    /** Whether the dialog is open. */
    open: boolean
    /** Called when the dialog requests to be closed. */
    onClose: () => void
    /** Dialog heading — defaults to "AI Disclaimer". */
    title?: string
    /** Body content rendered inside the dialog. */
    children: ReactNode
}

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(700),
            minWidth: pxToRem(700),
            color: theme.palette.common.black,
        },
    },
    title: {
        paddingBottom: theme.spacing(1),
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(24),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
    },
    icon: {
        width: pxToRem(24),
        height: pxToRem(24),
        flexShrink: 0,
    },
    content: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
    },
    divider: {
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
    },
    actionContainer: {
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(2.5),
    },
    closeButton: {
        backgroundColor: theme.palette.common.white,
        border: '1px solid #0098D6',
        color: '#0098D6',
        borderRadius: pxToRem(100),
        fontSize: pxToRem(16),
        padding: theme.spacing(0.5, 1.85),
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#F5F5F5',
        },
    },
}))

/**
 * Shared AI disclaimer dialog shell — purple AI icon in the title, dividers,
 * and a Close button. Pass feature-specific content as children.
 */
const AIDisclaimerDialog = ({
    open,
    onClose,
    title = 'AI Disclaimer',
    children,
}: AIDisclaimerDialogProps) => {
    const classes = useStyles()
    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="ai-disclaimer-dialog-title"
            data-testid="ai-disclaimer-dialog"
            maxWidth="lg"
            className={classes.dialog}
        >
            <DialogTitle id="ai-disclaimer-dialog-title" className={classes.title}>
                <AIIcon className={classes.icon} style={{ color: AI_ICON_COLOR }} aria-hidden="true" />
                {title}
            </DialogTitle>
            <Divider className={classes.divider} />
            <DialogContent className={classes.content}>
                {children}
            </DialogContent>
            <Divider className={classes.divider} />
            <DialogActions className={classes.actionContainer}>
                <Button
                    data-testid="btn-close-ai-disclaimer"
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AIDisclaimerDialog
